import React, { useState, useMemo, useEffect } from 'react';
import { ShoppingCart as ShoppingCartIcon, Search, Anchor } from 'lucide-react';
import ProductCard from './components/ProductCard';
import ShoppingCart from './components/ShoppingCart';
import AdminPanel from './components/AdminPanel';
import WelcomeModal from './components/WelcomeModal';
import Watermark from './components/Watermark';
import { generateUniqueId } from './utils/helpers';

const App = () => {
  const [products, setProducts] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [showWelcomeModal, setShowWelcomeModal] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);

  // ✅ Cargar productos desde el backend
  useEffect(() => {
    fetch("/backend/api/products.php")
      .then(res => res.json())
      .then(data => {
        setProducts(data);
        setIsLoadingProducts(false);
      })
      .catch(err => {
        console.error("Error cargando productos:", err);
        setIsLoadingProducts(false);
      });
  }, []);

  const handleNameSubmit = (name) => {
    setCustomerName(name);
    setShowWelcomeModal(false);
  };

  const handleAddToCart = (product, quantity) => {
    setCartItems(prevItems => {
      const existingItemIndex = prevItems.findIndex(item => item.id === product.id);
      if (existingItemIndex > -1) {
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex].quantity += quantity;
        return updatedItems;
      } else {
        return [...prevItems, { ...product, quantity, cartItemId: generateUniqueId() }];
      }
    });
    setIsCartOpen(true);
  };

  const handleUpdateCartQuantity = (cartItemId, newQuantity) => {
    setCartItems(prevItems =>
      prevItems.map(item =>
        item.cartItemId === cartItemId ? { ...item, quantity: newQuantity } : item
      ).filter(item => item.quantity > 0)
    );
  };

  const handleRemoveCartItem = (cartItemId) => {
    setCartItems(prevItems => prevItems.filter(item => item.cartItemId !== cartItemId));
  };

  const handleAddProduct = (newProductData) => {
    setProducts(prevProducts => [...prevProducts, newProductData]);
  };

  const handleUpdateProduct = (updatedProduct) => {
    setProducts(prevProducts =>
      prevProducts.map(product =>
        product.id === updatedProduct.id ? updatedProduct : product
      )
    );
  };

  const handleDeleteProduct = (productId) => {
    setProducts(prevProducts => prevProducts.filter(product => product.id !== productId));
  };

  const totalCartItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const filteredProducts = useMemo(() => {
    if (isLoadingProducts) return [];
    if (!searchTerm) {
      return products;
    }
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return products.filter(product =>
      product.name.toLowerCase().includes(lowercasedSearchTerm) ||
      product.description.toLowerCase().includes(lowercasedSearchTerm)
    );
  }, [products, searchTerm, isLoadingProducts]);

  if (isLoadingProducts) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600 text-sm">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 relative">
      <header className="bg-white shadow-sm py-3 px-4 sm:py-4 sm:px-6 flex flex-col sm:flex-row justify-between items-center sticky top-0 z-40 gap-3 sm:gap-0 border-b border-gray-100">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Anchor className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600" />
          Del Mar a Tu Mesa
        </h1>
        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 w-full sm:w-auto">
          <div className="relative w-full sm:w-auto">
            <input
              type="text"
              placeholder="Buscar productos..."
              className="pl-10 pr-4 py-2 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 w-full"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          </div>
          <button
            onClick={() => setIsCartOpen(true)}
            className="relative p-3 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200"
          >
            <ShoppingCartIcon className="w-6 h-6" />
            {totalCartItems > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                {totalCartItems.toFixed(0)}
              </span>
            )}
          </button>
          <button
            onClick={() => setIsAdminPanelOpen(true)}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300"
          >
            Admin
          </button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 sm:px-6 sm:py-12">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-8 sm:mb-10 text-center">
          Nuestros Productos Frescos
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {filteredProducts.length > 0 ? (
            filteredProducts.map(product => (
              <ProductCard key={product.id} product={product} onAddToCart={handleAddToCart} />
            ))
          ) : (
            <div className="col-span-full text-center py-10 sm:py-12 bg-white rounded-2xl shadow-lg border border-gray-100">
              <Search className="w-16 h-16 sm:w-20 sm:h-20 text-gray-300 mx-auto mb-4 sm:mb-6" />
              <p className="text-gray-500 text-base sm:text-lg font-medium">
                No encontramos ningún producto con "{searchTerm}". ¡Prueba con otra búsqueda!
              </p>
            </div>
          )}
        </div>
      </main>

      <Watermark />

      {isCartOpen && (
        <ShoppingCart
          cartItems={cartItems}
          onUpdateQuantity={handleUpdateCartQuantity}
          onRemoveItem={handleRemoveCartItem}
          onClose={() => setIsCartOpen(false)}
          customerName={customerName}
        />
      )}
      {isAdminPanelOpen && (
        <AdminPanel
          products={products}
          onAddProduct={handleAddProduct}
          onUpdateProduct={handleUpdateProduct}
          onDeleteProduct={handleDeleteProduct}
          onClose={() => setIsAdminPanelOpen(false)}
        />
      )}
      {showWelcomeModal && (
        <WelcomeModal onNameSubmit={handleNameSubmit} />
      )}
    </div>
  );
};

export default App;
